import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { signOut } from '@/lib/sessionService'
import { useTheme } from '@/shared/contexts/ThemeContext'
import { useContextKey, useUnit } from '@/modules/units/contexts/UnitContext'
import { routes } from '@/lib/appConfig'
import { BillingProfileModal } from '@/shared/components/ui'
import { UnitSelector } from '@/modules/units/UnitSelector'
import { TeamModal } from '@/modules/units'
import PwaInstallButton from '@/shared/components/ui/PwaInstallButton'
import { ContextIcon } from '@/shared/components/ui/ContextIcon'
import { getUserInvitations, acceptInvitation, declineInvitation } from '@/modules/units'
import { listEntries, isOverdue } from '@/modules/billing/entries/entriesService'
import { useTranslation, useUnitTranslation } from '@/shared/contexts/TranslationContext'
import { usePushNotifications } from '@/shared/hooks/usePushNotifications'

import type { UnitContext } from '@/modules/units/contexts/UnitContextTypes';
import { usePermissions } from '@/modules/units/usePermissions'

interface TopBarProps {
  userName?: string
  userId?: string
  currentUnitId?: string | null
  onMenuClick: () => void
}

export function TopBar({ userName, userId, currentUnitId, onMenuClick }: TopBarProps) {
  const router = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const { t, locale } = useTranslation()
  const { tu } = useUnitTranslation()
  const appContext = useContextKey() as UnitContext
  const { currentUnit, userRole } = useUnit()
  const isAccountingClient =
    appContext === 'accounting' && currentUnit?.attrs?.accounting_unit_type === 'client'
  const permissions = usePermissions(userRole)
  const isMember = userRole === 'viewer'
  const currencyByLocale: Record<string, string> = {
    'pt-BR': 'BRL',
    'en-US': 'USD',
    'es-ES': 'EUR',
  }
  const currency = currencyByLocale[locale] || 'BRL'
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [invitations, setInvitations] = useState<any[]>([])
  const [billingTotals, setBillingTotals] = useState<{
    payableOverdueCount:number; payableOverdueTotal:number;
    payableDueTodayCount:number; payableDueTodayTotal:number;
    receivableOverdueCount:number; receivableOverdueTotal:number;
    receivableDueTodayCount:number; receivableDueTodayTotal:number;
  }>({
    payableOverdueCount:0, payableOverdueTotal:0,
    payableDueTodayCount:0, payableDueTodayTotal:0,
    receivableOverdueCount:0, receivableOverdueTotal:0,
    receivableDueTodayCount:0, receivableDueTodayTotal:0,
  })
  const [loadingInvite, setLoadingInvite] = useState<string | null>(null)
  const [loadingBilling, setLoadingBilling] = useState(false)
  const { isSupported: isPushSupported, isSubscribed: isPushSubscribed, permission: pushPermission, subscribe: subscribePush, unsubscribe: unsubscribePush, loading: pushLoading } = usePushNotifications()

  useEffect(() => {
    if (userId) {
      console.log('[TopBar] Loading invitations for userId:', userId)
      loadInvitations()
      
      // Poll for new invitations every 30 seconds
      const interval = setInterval(loadInvitations, 30000)
      return () => clearInterval(interval)
    } else {
      console.log('[TopBar] No userId provided, skipping invitations load')
    }
  }, [userId])

  // Load billing totals (overdue + due today) for current unit and listen to global reload events
  useEffect(() => {
    if (!currentUnitId) return

    const loadBilling = async () => {
      setLoadingBilling(true)
      try {
        const today = new Date()
        const yyyy = today.getFullYear()
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const dd = String(today.getDate()).padStart(2, '0')
        const todayStr = `${yyyy}-${mm}-${dd}`

        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        const yyyyy = yesterday.getFullYear()
        const ymm = String(yesterday.getMonth() + 1).padStart(2, '0')
        const ydd = String(yesterday.getDate()).padStart(2, '0')
        const yesterdayStr = `${yyyyy}-${ymm}-${ydd}`

        // payable
        const payableOverdueEntries = await listEntries(currentUnitId, { entry_type: 'payable', dueDateTo: yesterdayStr, status: ['open'] })
        const payableDueTodayEntries = await listEntries(currentUnitId, { entry_type: 'payable', dueDateFrom: todayStr, dueDateTo: todayStr, status: ['open'] })

        // receivable
        const receivableOverdueEntries = await listEntries(currentUnitId, { entry_type: 'receivable', dueDateTo: yesterdayStr, status: ['open'] })
        const receivableDueTodayEntries = await listEntries(currentUnitId, { entry_type: 'receivable', dueDateFrom: todayStr, dueDateTo: todayStr, status: ['open'] })

        const payableOverdueTotal = (payableOverdueEntries || []).reduce((s:any,e:any) => s + (Number(e.amount) || 0), 0)
        const payableDueTodayTotal = (payableDueTodayEntries || []).reduce((s:any,e:any) => s + (Number(e.amount) || 0), 0)

        const receivableOverdueTotal = (receivableOverdueEntries || []).reduce((s:any,e:any) => s + (Number(e.amount) || 0), 0)
        const receivableDueTodayTotal = (receivableDueTodayEntries || []).reduce((s:any,e:any) => s + (Number(e.amount) || 0), 0)

        setBillingTotals({
          payableOverdueCount: (payableOverdueEntries || []).length,
          payableOverdueTotal,
          payableDueTodayCount: (payableDueTodayEntries || []).length,
          payableDueTodayTotal,
          receivableOverdueCount: (receivableOverdueEntries || []).length,
          receivableOverdueTotal,
          receivableDueTodayCount: (receivableDueTodayEntries || []).length,
          receivableDueTodayTotal,
        })
      } catch (err) {
        console.error('[TopBar] Error loading billing notifications', err)
      } finally {
        setLoadingBilling(false)
      }
    }

    loadBilling()
    const interval = setInterval(loadBilling, 30000)

    const onReload = () => loadBilling()
    window.addEventListener('reloadBilling', onReload)

    return () => {
      clearInterval(interval)
      window.removeEventListener('reloadBilling', onReload)
    }
  }, [currentUnitId])

  const loadInvitations = async () => {
    try {
      console.log('[TopBar] Fetching invitations...')
      const data = await getUserInvitations()
      console.log('[TopBar] Invitations received:', data)
      setInvitations(data)
    } catch (error: any) {
      console.error('[TopBar] Error loading invitations:', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
        fullError: error
      })
    }
  }

  const handleAcceptInvite = async (inviteId: string) => {
    try {
      setLoadingInvite(inviteId)
      await acceptInvitation(inviteId)
      await loadInvitations()
      
      // Trigger reload of units in UnitSelector
      window.dispatchEvent(new Event('reloadUnits'))
      
      // Show success message
      setShowNotifications(false)
      
      // Reload page after a short delay to show feedback
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      console.error('Error accepting invitation:', error)
      alert('Erro ao aceitar convite.')
      setLoadingInvite(null)
    }
  }

  const handleDeclineInvite = async (inviteId: string) => {
    try {
      setLoadingInvite(inviteId)
      await declineInvitation(inviteId)
      await loadInvitations()
    } catch (error) {
      console.error('Error declining invitation:', error)
      alert('Erro ao recusar convite.')
    } finally {
      setLoadingInvite(null)
    }
  }

  const handleLogout = async () => {
    await signOut()
    router(routes.login)
  }

  return (
    <header className="sticky top-0 z-[60] flex h-16 items-center justify-between border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 lg:px-6">
      {/* Mobile menu button */}
      <button
        onClick={onMenuClick}
        className="rounded-lg p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Accounting clients see their company name without a workspace selector. */}
      {userId && (
        <div className="block">
          {isAccountingClient ? (
            <div className="flex items-center gap-2 px-3 py-2">
              <ContextIcon context="accounting" className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              <span className="max-w-[200px] truncate text-sm font-semibold text-slate-900 dark:text-white">
                {currentUnit?.name}
              </span>
            </div>
          ) : (
            <UnitSelector
              userId={userId}
              currentUnitId={currentUnitId || null}
            />
          )}
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        {userId && (
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative rounded-lg p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              title={t('notifications.title')}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {((invitations.length || 0) + ((billingTotals.payableOverdueCount > 0 || billingTotals.receivableOverdueCount > 0 ? 1 : 0) + (billingTotals.payableDueTodayCount > 0 || billingTotals.receivableDueTodayCount > 0 ? 1 : 0))) > 0 && (
                <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {invitations.length + (billingTotals.payableOverdueCount > 0 || billingTotals.receivableOverdueCount > 0 ? 1 : 0) + (billingTotals.payableDueTodayCount > 0 || billingTotals.receivableDueTodayCount > 0 ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Notifications dropdown */}
            {showNotifications && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-[70]"
                  onClick={() => setShowNotifications(false)}
                />
                
                {/* Menu */}
                <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-96 z-[80] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
                  <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('notifications.title')}</h3>
                      {invitations.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                          {invitations.length} {invitations.length === 1 ? t('notifications.new') : t('notifications.newPlural')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                    {billingTotals.payableOverdueCount === 0 && billingTotals.payableDueTodayCount === 0 && billingTotals.receivableOverdueCount === 0 && billingTotals.receivableDueTodayCount === 0 && invitations.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('notifications.empty')}</p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {/* Payable - Overdue */}
                        {billingTotals.payableOverdueCount > 0 && (
                          <li className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center text-white">
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('notifications.billing.payable.overdue')}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{billingTotals.payableOverdueCount} â€¢ {(billingTotals.payableOverdueTotal || 0).toLocaleString(locale || 'pt-BR', { style: 'currency', currency })}</p>
                              </div>
                            </div>
                          </li>
                        )}

                        {/* Payable - Due Today */}
                        {billingTotals.payableDueTodayCount > 0 && (
                          <li className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <div className="h-8 w-8 rounded-full bg-amber-500 flex items-center justify-center text-white">
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('notifications.billing.payable.dueToday')}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{billingTotals.payableDueTodayCount} â€¢ {(billingTotals.payableDueTodayTotal || 0).toLocaleString(locale || 'pt-BR', { style: 'currency', currency })}</p>
                              </div>
                            </div>
                          </li>
                        )}

                        {/* Receivable - Overdue */}
                        {billingTotals.receivableOverdueCount > 0 && (
                          <li className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <div className="h-8 w-8 rounded-full bg-red-600 flex items-center justify-center text-white">
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('notifications.billing.receivable.overdue')}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{billingTotals.receivableOverdueCount} â€¢ {(billingTotals.receivableOverdueTotal || 0).toLocaleString(locale || 'pt-BR', { style: 'currency', currency })}</p>
                              </div>
                            </div>
                          </li>
                        )}

                        {/* Receivable - Due Today */}
                        {billingTotals.receivableDueTodayCount > 0 && (
                          <li className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <div className="h-8 w-8 rounded-full bg-amber-600 flex items-center justify-center text-white">
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{t('notifications.billing.receivable.dueToday')}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{billingTotals.receivableDueTodayCount} â€¢ {(billingTotals.receivableDueTodayTotal || 0).toLocaleString(locale || 'pt-BR', { style: 'currency', currency })}</p>
                              </div>
                            </div>
                          </li>
                        )}

                        {/* Invitation notifications */}
                        {invitations.map((invite) => (
                          <li key={invite.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white">
                                  <ContextIcon context={appContext} className="h-4 w-4" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {t('notifications.inviteFor', { unit: tu('singular').toLowerCase() })}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                  {invite.unit_name || tu('singular')}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  {new Date(invite.created_at).toLocaleDateString('pt-BR')}
                                </p>
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => handleAcceptInvite(invite.id)}
                                    disabled={loadingInvite === invite.id}
                                    className="px-3 py-1 text-xs font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {loadingInvite === invite.id ? t('notifications.accepting') : t('notifications.accept')}
                                  </button>
                                  <button
                                    onClick={() => handleDeclineInvite(invite.id)}
                                    disabled={loadingInvite === invite.id}
                                    className="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {t('notifications.decline')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* Push notifications + PWA install */}
                  <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 space-y-2">
                    {isPushSupported && pushPermission !== 'denied' && (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className={`h-4 w-4 shrink-0 ${isPushSubscribed ? 'text-emerald-500' : 'text-gray-400 dark:text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {isPushSubscribed ? t('notifications.push.enabled') : t('notifications.push.enable')}
                          </span>
                        </div>
                        <button
                          onClick={() => isPushSubscribed ? unsubscribePush() : subscribePush()}
                          disabled={pushLoading}
                          className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                            isPushSubscribed ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                            isPushSubscribed ? 'translate-x-4.5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                    )}
                    {isPushSupported && pushPermission === 'denied' && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t('notifications.push.blocked')}
                      </p>
                    )}
                    <div className="flex justify-center">
                      <PwaInstallButton />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-lg p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          title={theme === 'light' ? t('theme.enableDark') : t('theme.enableLight')}
        >
          {theme === 'light' ? (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )}
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="rounded-lg p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {showUserMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-[70]"
                onClick={() => setShowUserMenu(false)}
              />
              
              {/* Menu */}
              <div className="absolute right-0 mt-2 w-56 z-[80] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
                {userName && (
                  <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('user.hello')}!</p>
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowUserMenu(false)
                    setShowProfileModal(true)
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('user.myProfile')}
                </button>
                {(['billing', 'pricing'].includes(appContext || '') && currentUnit?.id && permissions.canManageTeam) && (
                  <button
                    onClick={() => {
                      setShowUserMenu(false)
                      setShowTeamModal(true)
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    {t('navigation.manageTeam') || 'Gerenciar Equipe'}
                  </button>
                )}

                <button
                  onClick={() => {
                    setShowUserMenu(false)
                    handleLogout()
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('user.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Team Modal */}
      {currentUnit?.id && (
        <TeamModal
          unitId={currentUnit.id}
          isOpen={showTeamModal}
          onClose={() => setShowTeamModal(false)}
        />
      )}

      {/* Billing Profile Modal */}
      <BillingProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
    </header>
  )
}
